import { FormRadioGroup, FormCheckbox, AutoSuggest, FormGroup, Input } from '@snsw/react-component-library';

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { Box, FlexBox } from '@onegov/react-component-library';
import { countryCodes } from './ContactMethodConstants';
import styles from '../../Page.module.scss';
import { setNativeValue } from 'shared/utils/Helpers';
import { PHONE_TYPES, PhoneNumber } from 'shared/utils/PhoneNumber';

const ContactMethod = React.forwardRef((props, ref) => {
  const { value: savedContactMethod = {}, onChange = () => {}, id = '', onErrorChange = () => {} } = props;

  const contactTypeOptions = [
    { value: PHONE_TYPES.MOBILE, label: 'Mobile' },
    { value: PHONE_TYPES.LANDLINE, label: 'Landline' },
  ];

  const { primary: storedPrimary = {}, secondary: storedSeconday = {} } = savedContactMethod;

  const [primary, setPrimary] = useState(storedPrimary);
  const [secondary, setSecondary] = useState(storedSeconday);
  const showSecondaryContact = !!secondary?.requiredType;

  const [primaryPhoneType, setPrimaryPhoneType] = useState(storedPrimary?.requiredType || '');
  const [primaryPrefix, setPrimaryPrefix] = useState(storedPrimary?.raw?.countryCode || '+61');
  const [primaryNumber, setPrimaryNumber] = useState(storedPrimary?.raw?.number || '');

  const [secondaryPhoneType, setSecondaryPhoneType] = useState(storedSeconday?.requiredType || '');
  const [secondaryPrefix, setSeconcaryPrefix] = useState(storedSeconday?.raw?.countryCode || '+61');
  const [secondaryNumber, setSecondaryNumber] = useState(storedSeconday?.raw?.number || '');

  const [showSecondaryContactMethod, setShowSecondaryContactMethod] = useState(showSecondaryContact || false);

  const [errors, setErrors] = useState({});

  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);

  const prefix1Ref = useRef();
  const prefix2Ref = useRef();

  useEffect(() => {
    //Autosuggest cannot be initialise in current version 1.32.1
    //Use this workaround for now
    if (prefix1Ref.current && primaryPrefix) {
      setNativeValue(prefix1Ref.current, primaryPrefix);
      prefix1Ref.current.dispatchEvent(new Event('input', { bubbles: true }));
    }

    if (prefix2Ref.current && secondaryPrefix) {
      setNativeValue(prefix2Ref.current, secondaryPrefix);
      prefix2Ref.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, [prefix1Ref.current, prefix2Ref.current, primaryPrefix, secondaryPrefix]);

  useEffect(() => {
    const contactMethod = {
      primary,
      ...(showSecondaryContactMethod && {
        secondary,
      }),
    };

    onChange(contactMethod);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [primary, secondary]);

  useEffect(() => {
    const primary = new PhoneNumber(primaryPrefix, primaryNumber, primaryPhoneType, false);
    setPrimary(primary);
  }, [primaryPhoneType, primaryPrefix, primaryNumber]);

  useEffect(() => {
    if (!showSecondaryContactMethod) {
      setSecondary({});
      return;
    }
    const secondary = new PhoneNumber(secondaryPrefix, secondaryNumber, secondaryPhoneType, false);
    setSecondary(secondary);
  }, [secondaryPhoneType, showSecondaryContactMethod, secondaryPrefix, secondaryNumber]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  const validators = [
    {
      fieldName: `primaryPhoneType_${id}`,
      fieldValue: primaryPhoneType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: `primaryPrefix_${id}`,
      fieldValue: primaryPrefix,
      isValid: (v) => !isEmpty(v) || isEmpty(primaryPhoneType),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `primaryNumber_${id}`,
      fieldValue: primaryNumber,
      isValid: (v) => !isEmpty(v) && isNumeric(v) && primary?.isValid(),
      errorMessage: (v) =>
        isEmpty(v) ? 'Please enter details' : !isNumeric(v) ? 'Numeric value is required' : 'Invalid phone number',
    },
    {
      fieldName: `secondaryPhoneType_${id}`,
      fieldValue: secondaryPhoneType,
      isValid: (v) => !isEmpty(v) || !showSecondaryContactMethod, //if secondary contact unchecked, mark as valid
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: `secondaryPrefix_${id}`,
      fieldValue: secondaryPrefix,
      isValid: (v) => !isEmpty(v) || !showSecondaryContactMethod || isEmpty(secondaryPhoneType), //if secondary contact unchecked, mark as valid,
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `secondaryNumber_${id}`,
      fieldValue: secondaryNumber,
      isValid: (v) =>
        !showSecondaryContactMethod ||
        (!isEmpty(v) && isNumeric(v) && secondary?.isValid()) ||
        isEmpty(secondaryPhoneType), //if secondary contact unchecked, mark as valid,
      errorMessage: (v) =>
        isEmpty(v) ? 'Please enter details' : !isNumeric(v) ? 'Numeric value is required' : 'Invalid phone number',
    },
  ];

  const onChangeContactMethod = (v, contactType) => {
    if (contactType === 'primary') {
      setPrimaryPhoneType(v);
    } else if (contactType === 'secondary') {
      setSecondaryPhoneType(v);
    }
  };

  const onChangePrefix = (v, contactType) => {
    if (contactType === 'primary') {
      setPrimaryPrefix(v);
    } else if (contactType === 'secondary') {
      setSeconcaryPrefix(v);
    }
  };

  const onChangeNumber = (v, contactType) => {
    if (contactType === 'primary') {
      setPrimaryNumber(v);
    } else if (contactType === 'secondary') {
      setSecondaryNumber(v);
    }
  };

  const onCheckedSecondaryContact = () => {
    setShowSecondaryContactMethod(!showSecondaryContactMethod);
  };

  const displayContactMethodOptions = (id, contactType) => {
    let value = '';
    let hasError = false;
    let errorMessage = '';
    let inputId = '';

    if (contactType === 'primary') {
      value = primaryPhoneType;
      hasError = showErrors && errors[`primaryPhoneType_${id}`]?.invalid;
      errorMessage = errors[`primaryPhoneType_${id}`]?.errorMessage;
      inputId = `primaryPhoneType_${id}`;
    } else if (contactType === 'secondary') {
      value = secondaryPhoneType;
      hasError = showErrors && errors[`secondaryPhoneType_${id}`]?.invalid;
      errorMessage = errors[`secondaryPhoneType_${id}`]?.errorMessage;
      inputId = `secondaryPhoneType_${id}`;
    }

    return (
      <FormRadioGroup
        id={inputId}
        name={inputId}
        legend="Preferred contact method"
        vertical={true}
        options={contactTypeOptions}
        onChange={(v) => {
          onChangeContactMethod(v, contactType);
        }}
        value={value}
        errorMessage={errorMessage}
        hasError={hasError}
        margin={{ top: 'xl' }}
      />
    );
  };

  const displayContactMethodFields = (id, contactType) => {
    let prefix = '';
    let number = '';
    let prefixHasError = false;
    let prefixErrorMessage = '';
    let numberHasError = false;
    let numberErrorMessage = '';
    let prefixInputId = '';
    let numberInputId = '';
    let showFields = false;

    if (contactType === 'primary') {
      prefix = primaryPrefix;
      number = primaryNumber;
      prefixInputId = `primaryPrefix_${id}`;
      numberInputId = `primaryNumber_${id}`;
      prefixHasError = showErrors && errors[`primaryPrefix_${id}`]?.invalid;
      numberHasError = showErrors && errors[`primaryNumber_${id}`]?.invalid;
      prefixErrorMessage = errors[`primaryPrefix_${id}`]?.errorMessage;
      numberErrorMessage = errors[`primaryNumber_${id}`]?.errorMessage;
      showFields = !isEmpty(primaryPhoneType);
    } else if (contactType == 'secondary') {
      prefix = secondaryPrefix;
      number = secondaryNumber;
      prefixInputId = `secondaryPrefix_${id}`;
      numberInputId = `secondaryNumber_${id}`;
      prefixHasError = showErrors && errors[`secondaryPrefix_${id}`]?.invalid;
      numberHasError = showErrors && errors[`secondaryNumber_${id}`]?.invalid;
      prefixErrorMessage = errors[`secondaryPrefix_${id}`]?.errorMessage;
      numberErrorMessage = errors[`secondaryNumber_${id}`]?.errorMessage;
      showFields = !isEmpty(secondaryPhoneType);
    }

    return showFields ? (
      <>
        <div className={styles.marginTop2rem}>
          {/* <span className={styles.helpMsg}>Mobile or landline</span> */}
          <FlexBox gap={1}>
            <Box flex="0 0 100px">
              <AutoSuggest
                id={prefixInputId}
                name={prefixInputId}
                suggestions={countryCodes}
                onSelect={(suggestion) => {
                  onChangePrefix(suggestion, contactType);
                }}
                onBlur={(suggestion, value, highlighted) => {
                  onChangePrefix(value, contactType);
                }}
                style={{ marginTop: 0 }}
                className={styles.noMarginT}
                value={prefix}
                suggestion={prefix}
                hasError={prefixHasError}
                errorMessage={prefixErrorMessage}
                inputRef={contactType === 'primary' ? prefix1Ref : prefix2Ref}
              />
            </Box>
            <Box flex="1">
              <FormGroup
                id={numberInputId}
                isRequired
                label=" "
                className={styles.noMarginT}
                hasError={numberHasError}
                errorMessage={numberErrorMessage}
              >
                <Input onChange={(e) => onChangeNumber(e.target.value, contactType)} value={number} inputWidth={'md'} />
              </FormGroup>
            </Box>
          </FlexBox>
        </div>
      </>
    ) : null;
  };

  if (ref) {
    ref.current = {
      validate: () => {
        const isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  return (
    <>
      {displayContactMethodOptions(id, 'primary')}
      {displayContactMethodFields(id, 'primary')}
      {primaryPhoneType && (
        <FormCheckbox
          id={`${props.id}_addSecondary`}
          name={`${props.id}_addSecondary`}
          label="I'd like to add secondary contact number (if needed)"
          onChange={onCheckedSecondaryContact}
          checked={showSecondaryContactMethod}
          value="secondaryContact"
          margin={{ top: 'md' }}
        />
      )}
      {showSecondaryContactMethod && displayContactMethodOptions(id, 'secondary')}
      {showSecondaryContactMethod && displayContactMethodFields(id, 'secondary')}
    </>
  );
});

ContactMethod.displayName = 'ContactMethod';

ContactMethod.propTypes = {
  onChange: PropTypes.func,
};

export { ContactMethod };
